import React, { useEffect, useRef, useState } from 'react';
import AdminIcon from './AdminIcon';
import AcquirerIdentity from './AcquirerIdentity';
import PagamentoDetalhes, { FormasResumo, SituacaoBadge, moeda } from './PagamentoDetalhes';

const FILTROS_INICIAIS = {
  busca: '', situacao: 'com_pagamento', forma: '', adquirente: '', aplicativo_id: '', data_inicio: '', data_fim: '',
  somente_nsu: false, cliente: '', empresa_id: ''
};

const CARTOES = [
  { situacao: 'com_pagamento', rotulo: 'Total pago', classe: 'pago' },
  { situacao: 'conciliado', rotulo: 'Conciliados', classe: 'ok' },
  { situacao: 'divergente', rotulo: 'Divergentes', classe: 'warn' },
  { situacao: 'sem_pagamento', rotulo: 'Sem pagamento', classe: 'neutral' }
];

const COLUNAS_ORDENAVEIS = { numero: 'Pedido', cliente: 'Cliente', data: 'Data e hora', total: 'Valor do pedido', pago: 'Valor pago' };

function Ordenavel({ coluna, ordem, direcao, onOrdenar, className }) {
  const ativa = ordem === coluna;
  return (
    <th className={className} aria-sort={ativa ? (direcao === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button type="button" className={`pg-sort ${ativa ? `is-active is-${direcao}` : ''}`} onClick={() => onOrdenar(coluna)}>
        {COLUNAS_ORDENAVEIS[coluna]}
      </button>
    </th>
  );
}

function Maquininha({ pedido }) {
  const ids = pedido.pagamentos.map(p => p.nsu || p.nsu_pix || p.mp_id_pagamento).filter(Boolean);
  return (
    <div className="pg-maquininha">
      <AcquirerIdentity name={pedido.adquirente} />
      {ids.length > 0 && <span className="pg-nsu">NSU {ids[0]}{ids.length > 1 ? ` +${ids.length - 1}` : ''}</span>}
    </div>
  );
}

export default function Pagamentos({ api, isSoftwareHouse }) {
  const [clientes, setClientes] = useState([]);
  const [clienteSelecionado, setClienteSelecionado] = useState(localStorage.getItem('clienteSelecionadoId') || '');
  const [filtros, setFiltros] = useState(FILTROS_INICIAIS);
  const [busca, setBusca] = useState('');
  const [opcoes, setOpcoes] = useState({ formas: [], aplicativos: [], adquirentes: [] });
  const [visao, setVisao] = useState('pedidos');
  const [ordem, setOrdem] = useState('data');
  const [direcao, setDirecao] = useState('desc');
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(25);
  const [dados, setDados] = useState(null);
  const [resumo, setResumo] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [abertos, setAbertos] = useState(() => new Set());
  const requisicao = useRef(0);

  const mostrarEmpresa = isSoftwareHouse && !clienteSelecionado;

  const identificacaoCliente = registro => [
    registro.codigo_cliente ? `Cód. ${registro.codigo_cliente}` : 'Sem código',
    mostrarEmpresa ? registro.empresa_nome : null
  ].filter(Boolean).join(' · ');

  const alterarFiltro = alteracoes => {
    setFiltros(atuais => ({ ...atuais, ...alteracoes }));
    setPagina(1);
  };

  useEffect(() => {
    const espera = setTimeout(() => {
      setFiltros(atuais => (atuais.busca === busca ? atuais : { ...atuais, busca }));
      if (busca !== filtros.busca) setPagina(1);
    }, 350);
    return () => clearTimeout(espera);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busca]);

  useEffect(() => {
    if (!isSoftwareHouse) return;
    api.get('/clientes').then(lista => setClientes(Array.isArray(lista) ? lista : [])).catch(() => setClientes([]));
  }, [api, isSoftwareHouse]);

  useEffect(() => {
    api.get('/pagamentos/filtros')
      .then(resposta => { if (resposta.success) setOpcoes(resposta); })
      .catch(() => setOpcoes({ formas: [], aplicativos: [], adquirentes: [] }));
  }, [api, clienteSelecionado]);

  useEffect(() => {
    const atual = ++requisicao.current;
    const parametros = new URLSearchParams({ pagina, por_pagina: porPagina, ordem, direcao });
    Object.entries(filtros).forEach(([chave, valor]) => { if (valor) parametros.set(chave, valor); });
    setCarregando(true);
    setErro('');
    const rota = visao === 'clientes' ? '/pagamentos/clientes' : '/pagamentos';
    api.get(`${rota}?${parametros}`)
      .then(resposta => {
        if (atual !== requisicao.current) return;
        if (!resposta.success) throw new Error(resposta.error || 'Não foi possível carregar os pagamentos');
        setDados({ ...resposta, visao });
        if (resposta.resumo) setResumo(resposta.resumo);
      })
      .catch(e => { if (atual === requisicao.current) setErro(e.message); })
      .finally(() => { if (atual === requisicao.current) setCarregando(false); });
  }, [api, filtros, visao, ordem, direcao, pagina, porPagina, clienteSelecionado]);

  const selecionarCliente = id => {
    if (id) {
      localStorage.setItem('clienteSelecionadoId', id);
      const cliente = clientes.find(item => String(item.id) === String(id));
      if (cliente) localStorage.setItem('clienteSelecionadoNome', cliente.nome_fantasia || cliente.razao_social);
    } else {
      localStorage.removeItem('clienteSelecionadoId');
      localStorage.removeItem('clienteSelecionadoNome');
    }
    setClienteSelecionado(id);
    setFiltros(atuais => ({ ...atuais, aplicativo_id: '', forma: '', adquirente: '', cliente: '', empresa_id: '' }));
    setPagina(1);
  };

  const ordenar = coluna => {
    if (ordem === coluna) setDirecao(atual => (atual === 'asc' ? 'desc' : 'asc'));
    else { setOrdem(coluna); setDirecao('desc'); }
    setPagina(1);
  };

  const alternar = id => setAbertos(atuais => {
    const proximos = new Set(atuais);
    if (!proximos.delete(id)) proximos.add(id);
    return proximos;
  });

  const trocarVisao = nova => { setVisao(nova); setPagina(1); };

  const verPedidosDoCliente = cliente => {
    alterarFiltro({ cliente: cliente.nome_cliente, empresa_id: mostrarEmpresa ? String(cliente.company_id) : '' });
    trocarVisao('pedidos');
  };

  const limpar = () => { setFiltros(FILTROS_INICIAIS); setBusca(''); setPagina(1); };

  const valorCartao = situacao => {
    if (!resumo) return '—';
    if (situacao === 'com_pagamento') return moeda.format(resumo.total_pago);
    if (situacao === 'conciliado') return resumo.conciliados;
    return situacao === 'divergente' ? resumo.divergentes : resumo.sem_pagamento;
  };

  const legendaCartao = situacao => {
    if (!resumo) return '';
    if (situacao === 'com_pagamento') return `${resumo.conciliados + resumo.divergentes} pedidos · ${resumo.pagamentos} pagamentos`;
    if (situacao === 'sem_pagamento') return `de ${resumo.pedidos} pedidos`;
    return situacao === 'conciliado' ? 'Valor pago igual ao do pedido' : 'Valor pago diferente do pedido';
  };

  // Ao trocar de visão, a resposta anterior tem outro formato e não pode ser exibida.
  const itens = dados?.visao === visao ? dados.itens : [];
  const paginacao = dados?.visao === visao ? dados.paginacao : null;

  return (
    <div className="pg">
      <div className="page-header">
        <div><span className="eyebrow">FINANCEIRO</span><h2>Pedidos</h2><p>Concilie pedidos e pagamentos recebidos dos aplicativos, por cliente.</p></div>
      </div>

      {isSoftwareHouse && clientes.length > 0 && (
        <div className="client-selector-bar">
          <label htmlFor="pg-cliente">Cliente:</label>
          <select id="pg-cliente" value={clienteSelecionado} onChange={e => selecionarCliente(e.target.value)}>
            <option value="">Todos os clientes</option>
            {clientes.map(c => <option key={c.id} value={c.id}>{c.nome_fantasia || c.razao_social}</option>)}
          </select>
        </div>
      )}

      <section className="pg-kpis" aria-label="Resumo da conciliação">
        {CARTOES.map(cartao => (
          <button type="button" key={cartao.situacao} aria-pressed={filtros.situacao === cartao.situacao}
            className={`pg-kpi pg-kpi-${cartao.classe} ${filtros.situacao === cartao.situacao ? 'is-active' : ''}`}
            onClick={() => alterarFiltro({ situacao: cartao.situacao })}>
            <span>{cartao.rotulo}</span>
            <strong>{valorCartao(cartao.situacao)}</strong>
            <small>{legendaCartao(cartao.situacao)}</small>
          </button>
        ))}
      </section>

      <section className="pg-panel">
        <div className="pg-toolbar">
          <div className="pg-search">
            <AdminIcon name="search" />
            <input type="search" placeholder="Buscar por pedido, cliente, NSU, maquininha ou bandeira" aria-label="Buscar pagamentos"
              value={busca} onChange={e => setBusca(e.target.value)} />
          </div>
          <div className="pg-views" role="group" aria-label="Forma de visualização">
            <button type="button" aria-pressed={visao === 'pedidos'} className={visao === 'pedidos' ? 'is-active' : ''} onClick={() => trocarVisao('pedidos')}>Por pedido</button>
            <button type="button" aria-pressed={visao === 'clientes'} className={visao === 'clientes' ? 'is-active' : ''} onClick={() => trocarVisao('clientes')}>Por cliente</button>
          </div>
        </div>

        <div className="pg-filters">
          <label>Forma de pagamento
            <select value={filtros.forma} onChange={e => alterarFiltro({ forma: e.target.value })}>
              <option value="">Todas</option>
              {opcoes.formas.map(forma => <option key={forma} value={forma}>{forma}</option>)}
            </select>
          </label>
          <label>Adquirente
            <select value={filtros.adquirente} onChange={e => alterarFiltro({ adquirente: e.target.value })}>
              <option value="">Todas</option>
              {opcoes.adquirentes.map(adquirente => <option key={adquirente} value={adquirente}>{adquirente}</option>)}
            </select>
          </label>
          <label>Aplicativo
            <select value={filtros.aplicativo_id} onChange={e => alterarFiltro({ aplicativo_id: e.target.value })}>
              <option value="">Todos</option>
              {opcoes.aplicativos.map(app => <option key={app.id} value={app.id}>{app.nome}</option>)}
            </select>
          </label>
          <label>De
            <input type="date" value={filtros.data_inicio} max={filtros.data_fim || undefined} onChange={e => alterarFiltro({ data_inicio: e.target.value })} />
          </label>
          <label>Até
            <input type="date" value={filtros.data_fim} min={filtros.data_inicio || undefined} onChange={e => alterarFiltro({ data_fim: e.target.value })} />
          </label>
          <label className="pg-check">
            <input type="checkbox" checked={filtros.somente_nsu} onChange={e => alterarFiltro({ somente_nsu: e.target.checked })} />
            Somente com NSU
          </label>
          <button type="button" className="pg-clear" onClick={limpar}>Limpar filtros</button>
        </div>

        {filtros.cliente && (
          <div className="pg-active-filter">
            Cliente: <strong>{filtros.cliente}</strong>
            <button type="button" aria-label="Remover filtro de cliente" onClick={() => alterarFiltro({ cliente: '', empresa_id: '' })}>×</button>
          </div>
        )}

        {erro && <div className="pg-error" role="alert">{erro}</div>}

        <div className={`pg-table-wrap ${carregando ? 'is-loading' : ''}`} aria-busy={carregando}>
          {visao === 'pedidos' ? (
            <table className="pg-table">
              <thead>
                <tr>
                  <th aria-label="Detalhes do pagamento" />
                  <Ordenavel coluna="numero" ordem={ordem} direcao={direcao} onOrdenar={ordenar} />
                  <Ordenavel coluna="cliente" ordem={ordem} direcao={direcao} onOrdenar={ordenar} />
                  <Ordenavel coluna="data" ordem={ordem} direcao={direcao} onOrdenar={ordenar} />
                  <th>Formas de pagamento</th>
                  <th>Maquininha</th>
                  <Ordenavel coluna="total" ordem={ordem} direcao={direcao} onOrdenar={ordenar} className="pg-right" />
                  <Ordenavel coluna="pago" ordem={ordem} direcao={direcao} onOrdenar={ordenar} className="pg-right" />
                  <th>Situação</th>
                </tr>
              </thead>
              <tbody>
                {itens.map(pedido => {
                  const aberto = abertos.has(pedido.id);
                  return (
                    <React.Fragment key={pedido.id}>
                      <tr className={`pg-row ${aberto ? 'is-open' : ''}`} onClick={() => alternar(pedido.id)}>
                        <td className="pg-toggle-cell">
                          <button type="button" className="pay-toggle" aria-expanded={aberto} aria-controls={`pg-detalhe-${pedido.id}`}
                            aria-label={`${aberto ? 'Ocultar' : 'Ver'} pagamento do pedido ${pedido.numero_pedido}`}
                            onClick={event => { event.stopPropagation(); alternar(pedido.id); }}>
                            <AdminIcon name="chevron" />
                          </button>
                        </td>
                        <td data-label="Pedido"><strong>#{pedido.numero_pedido}</strong><small>{pedido.aplicativo_nome}</small></td>
                        <td data-label="Cliente"><strong className="pg-name">{pedido.nome_cliente}</strong><small className="pg-sub">{identificacaoCliente(pedido)}</small></td>
                        <td data-label="Data e hora" className="pg-nowrap">{pedido.data_venda || '-'}</td>
                        <td data-label="Formas de pagamento"><FormasResumo pedido={pedido} /></td>
                        <td data-label="Maquininha"><Maquininha pedido={pedido} /></td>
                        <td data-label="Valor do pedido" className="pg-right pg-money">{moeda.format(pedido.total)}</td>
                        <td data-label="Valor pago" className="pg-right pg-money"><strong>{pedido.pagamentos.length ? moeda.format(pedido.total_pago) : '—'}</strong></td>
                        <td data-label="Situação"><SituacaoBadge situacao={pedido.situacao} /></td>
                      </tr>
                      {aberto && <tr className="pg-detail" id={`pg-detalhe-${pedido.id}`}><td colSpan={9}><PagamentoDetalhes pedido={pedido} /></td></tr>}
                    </React.Fragment>
                  );
                })}
                {!itens.length && !carregando && <tr><td className="pg-empty" colSpan={9}>Nenhum pedido encontrado com esses filtros.</td></tr>}
              </tbody>
            </table>
          ) : (
            <table className="pg-table">
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th className="pg-right">Pedidos</th>
                  <th className="pg-right">Valor dos pedidos</th>
                  <th className="pg-right">Valor pago</th>
                  <th className="pg-right">Conciliados</th>
                  <th className="pg-right">Divergentes</th>
                  <th>Última venda</th>
                  <th aria-label="Ações" />
                </tr>
              </thead>
              <tbody>
                {itens.map(cliente => (
                  <tr className="pg-row" key={`${cliente.company_id}-${cliente.nome_cliente}`} onClick={() => verPedidosDoCliente(cliente)}>
                    <td data-label="Cliente"><strong className="pg-name">{cliente.nome_cliente}</strong><small className="pg-sub">{identificacaoCliente(cliente)}</small></td>
                    <td data-label="Pedidos" className="pg-right pg-money">{cliente.pedidos}</td>
                    <td data-label="Valor dos pedidos" className="pg-right pg-money">{moeda.format(cliente.total_vendido)}</td>
                    <td data-label="Valor pago" className="pg-right pg-money"><strong>{moeda.format(cliente.total_pago)}</strong></td>
                    <td data-label="Conciliados" className="pg-right pg-money">{cliente.conciliados}</td>
                    <td data-label="Divergentes" className="pg-right pg-money">{cliente.divergentes ? <span className="pg-alert">{cliente.divergentes}</span> : 0}</td>
                    <td data-label="Última venda" className="pg-nowrap">{cliente.ultima_venda || '-'}</td>
                    <td className="pg-right"><button type="button" className="pg-link" onClick={event => { event.stopPropagation(); verPedidosDoCliente(cliente); }}>Ver pedidos</button></td>
                  </tr>
                ))}
                {!itens.length && !carregando && <tr><td className="pg-empty" colSpan={8}>Nenhum cliente encontrado com esses filtros.</td></tr>}
              </tbody>
            </table>
          )}
        </div>

        {paginacao && (
          <footer className="pg-pagination">
            <span>{paginacao.total} {paginacao.total === 1 ? 'registro' : 'registros'}</span>
            <label>Por página
              <select value={porPagina} onChange={e => { setPorPagina(Number(e.target.value)); setPagina(1); }}>
                {[25, 50, 100].map(tamanho => <option key={tamanho} value={tamanho}>{tamanho}</option>)}
              </select>
            </label>
            <div>
              <button type="button" disabled={paginacao.pagina <= 1} onClick={() => setPagina(paginacao.pagina - 1)}>Anterior</button>
              <strong>Página {paginacao.pagina} de {paginacao.total_paginas}</strong>
              <button type="button" disabled={paginacao.pagina >= paginacao.total_paginas} onClick={() => setPagina(paginacao.pagina + 1)}>Próxima</button>
            </div>
          </footer>
        )}
      </section>
    </div>
  );
}
